// @ts-nocheck
// TODO: tighten types after migration
import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { robustFetch } from '../utils/apiUtils';
import { parseChapters, getCurrentChapter } from '../utils/chapterParser';
import { getPodcastProgress, upsertPodcastProgress, syncPodcastProgressToServer, hydratePodcastProgressFromServer } from './playbackProgress';
import './PodcastHome.css';

const STORAGE_KEY = 'globalPodcastPlayer';
const VOLUME_STORAGE_KEY = 'globalPodcastPlayerVolume';

// Cross-subdomain cookie helpers (allows sharing playback state across podcast, blog, store, etc.)
function getRootDomain() {
    if (typeof window === 'undefined') return '';
    const host = window.location.hostname;
    if (host.includes('harivishnu.in')) {
        return '; domain=.harivishnu.in';
    }
    if (host === 'localhost' || host.endsWith('.localhost')) {
        return '; domain=.localhost';
    }
    return '';
}

function setCrossSubdomainState(key, value) {
    if (typeof document === 'undefined') return;
    try {
        const domainStr = getRootDomain();
        const encoded = encodeURIComponent(value);
        document.cookie = `${key}=${encoded}; path=/${domainStr}; max-age=86400; SameSite=Lax`;
    } catch (_) {}
}

function getCrossSubdomainState(key) {
    if (typeof document === 'undefined') return null;
    try {
        const match = document.cookie.match(new RegExp('(^|;\\s*)' + key + '=([^;]*)'));
        return match ? decodeURIComponent(match[2]) : null;
    } catch (_) {
        return null;
    }
}

function removeCrossSubdomainState(key) {
    if (typeof document === 'undefined') return;
    try {
        const domainStr = getRootDomain();
        document.cookie = `${key}=; path=/${domainStr}; max-age=0; SameSite=Lax`;
    } catch (_) {}
}

// Memoized TranscriptWord to prevent re-rendering 5000+ words on every tick
const TranscriptWord = React.memo(({ word, isCurrent, isCompleted, index }) => {
    // Check line break logic inside component or pass it? passing it is easier but we computed it in parent loop.
    // Let's pass 'needsLineBreak' as prop to keep it pure.
    // Wait, the original code had: {needsLineBreak && <br />} <span ...>
    // So distinct elements. We can include the break here if we pass the flag.
    
    return (
        <span
            id={`transcript-word-${index}`}
            className={`transcript-word ${isCurrent ? 'active' : ''} ${isCompleted ? 'completed' : ''}`}
            style={{
                color: isCurrent ? '#47b5ff' : (isCompleted ? '#47b5ff' : 'inherit'),
                fontWeight: 'normal',
                textShadow: isCurrent ? '0 0 1px rgba(0, 119, 255, 0.3)' : 'none',
                transition: 'all 0.1s ease',
                display: 'inline-block',
                marginRight: '4px',
                cursor: 'pointer'
            }}
        >
            {word.word}
        </span>
    );
}, (prev, next) => {
    return prev.isCurrent === next.isCurrent && prev.isCompleted === next.isCompleted && prev.word === next.word;
});

// Optimized Transcript View to handle large arrays (10,000+ words)
const TranscriptContent = React.memo(({ words, currentIndex, liveWindowStart, liveWindowEnd, isNewFormat, linesToShow }) => {
    if (!isNewFormat) {
        if (!linesToShow || linesToShow.length === 0) return null;
        return linesToShow.map(({ index, type, line }) => (
            <div key={index} id={`transcript-line-${index}`} className={`transcript-line ${type}`}>
                <span className="transcript-timestamp">
                    {line.startTime !== undefined 
                        ? (line.startTime.toFixed(2) + 's') 
                        : (line.timestamp || '0:00')}:
                </span>
                <span className="transcript-text">{line.text || line.line}</span>
            </div>
        ));
    }

    if (!words || words.length === 0) return null;

    // Group words into paragraphs of ~50 words to reduce Reconciliation work
    const paragraphs = useMemo(() => {
        const result = [];
        const chunkSize = 50;
        for (let i = 0; i < words.length; i += chunkSize) {
            result.push({
                startIndex: i,
                endIndex: Math.min(i + chunkSize - 1, words.length - 1),
                words: words.slice(i, i + chunkSize)
            });
        }
        return result;
    }, [words]);

    return (
        <div style={{ lineHeight: '1.8', fontSize: '15px', padding: '10px', wordBreak: 'break-word' }}>
            {paragraphs.map((para) => (
                <TranscriptParagraph 
                    key={para.startIndex}
                    words={para.words}
                    startIndex={para.startIndex}
                    currentIndex={currentIndex}
                    liveWindowStart={liveWindowStart}
                    liveWindowEnd={liveWindowEnd}
                />
            ))}
        </div>
    );
});

// Memoized Paragraph to further optimize reconciliation
const TranscriptParagraph = React.memo(({ words, startIndex, currentIndex, liveWindowStart, liveWindowEnd }) => {
    // Only re-render the paragraph if the active word is inside it OR was previously inside it
    // Or if it's generally visible (but let's keep it simple with index check)
    const isActive = currentIndex >= startIndex && currentIndex < startIndex + words.length;
    const wasActive = useRef(false);
    
    // We should re-render if it IS active or if it WAS active (to clear previous highlight)
    // Actually, memoization takes care of this if we just pass the right props.
    
    return (
        <div className={`transcript-paragraph ${isActive ? 'active' : ''}`} style={{ marginBottom: '16px' }}>
            {words.map((word, idx) => {
                const globalIndex = startIndex + idx;
                const isCurrent = globalIndex === currentIndex;
                const isCompleted = currentIndex >= 0 && globalIndex <= currentIndex;
                
                // Pre-calculating line breaks every 15s - shifted slightly to avoid mid-paragraph breaks if possible
                let needsLineBreak = false;
                if (globalIndex > 0 && globalIndex % 20 === 0) { // Add a physical gap every 20 words for layout breathing room
                     needsLineBreak = true;
                }

                return (
                    <React.Fragment key={`w-${globalIndex}`}>
                        {needsLineBreak && <span style={{ display: 'block', height: '8px' }} />}
                        <TranscriptWord 
                            word={word} 
                            isCurrent={isCurrent} 
                            isCompleted={isCompleted}
                            index={globalIndex} 
                        />
                    </React.Fragment>
                );
            })}
        </div>
    );
});

// Custom equality check for TapeScroller
const areTapePropsEqual = (prevProps, nextProps) => {
    // If other critical props change, re-render
    if (prevProps.isPlaying !== nextProps.isPlaying || 
        prevProps.duration !== nextProps.duration || 
        prevProps.tapeMarkers !== nextProps.tapeMarkers) {
        return false;
    }
    // If playing, we DO NOT care about currentTimeVal changes for re-rendering
    // because the view is updated via RAF directly manipulating the DOM.
    // We only care if we are PAUSED, where we need the prop to update the position.
    if (nextProps.isPlaying) {
        return true; // Pretend props are equal even if currentTimeVal updated
    }
    // If paused, we need to check currentTimeVal
    return prevProps.currentTimeVal === nextProps.currentTimeVal;
};

// Memoized TapeScroller component to prevent re-renders from interfering with RAF animation
const TapeScroller = React.memo(({ audioRef, isPlaying, isDragging, duration, tapeMarkers, currentTimeVal }) => {
    const tapeScrollerRef = useRef(null);
    const animationFrameRef = useRef(null);
    const PIXELS_PER_SECOND = 6;

    const formatTapeTimeInternal = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    // 1. Smooth Tape Animation Loop (reads real audio time continuously)
    useEffect(() => {
    const animateTape = () => {
        if (!isDragging && audioRef.current && tapeScrollerRef.current) {
            const currentPlace = audioRef.current.currentTime * PIXELS_PER_SECOND;
            tapeScrollerRef.current.style.transform = `translateX(-${currentPlace}px)`;
            animationFrameRef.current = requestAnimationFrame(animateTape);
        } else if (isDragging) {
            // If dragging, we stop the loop to prevent fighting with the drag handler
            animationFrameRef.current = requestAnimationFrame(animateTape);
        }
    };

        // Start the loop
        animationFrameRef.current = requestAnimationFrame(animateTape);

        return () => {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
            }
        };
    }, [audioRef]);

    // 2. Static Position Update (Runs when PAUSED or when scrubbing)
    useEffect(() => {
        if (isPlaying) return; // Do not interfere with the animation loop

        if (tapeScrollerRef.current) {
            // Priority to currentTimeVal prop during dragging/scrolling
            const time = currentTimeVal || 0;
            const currentPlace = time * PIXELS_PER_SECOND;
            tapeScrollerRef.current.style.transform = `translateX(-${currentPlace}px)`;
        }
    }, [isPlaying, currentTimeVal]); 

    return (
        <div 
            id="tape-scroller-inner"
            ref={tapeScrollerRef}
            className="tape-scroller"
            style={{ 
                width: `${duration * PIXELS_PER_SECOND}px`,
                position: 'relative',
                height: '100%',
                willChange: 'transform'
            }}
        >
            {/* Tape Ticks Generated by Background Gradient in CSS for performance */}
            <div className="tape-ticks" style={{ width: `${Math.max(duration * PIXELS_PER_SECOND, 2000)}px` }}>
                {/* Major Time Markers */}
                {tapeMarkers.map((time) => (
                    <div 
                        key={time} 
                        className="tape-time-marker"
                        style={{ left: `${time * PIXELS_PER_SECOND}px` }}
                    >
                        {formatTapeTimeInternal(time)}
                    </div>
                ))}
            </div>
        </div>
    );
}, areTapePropsEqual);

export default function GlobalAudioPlayer() {
    const navigate = useNavigate();
    const [currentPodcast, setCurrentPodcast] = useState(null);
    const [correctedThumbnailUrl, setCorrectedThumbnailUrl] = useState(null);
    const [audioPlayerVisible, setAudioPlayerVisible] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [isPlaying, setIsPlaying] = useState(false);
    const [displayTimeSec, setDisplayTimeSec] = useState(0);
    const [allPodcasts, setAllPodcasts] = useState([]); // Store all podcasts for navigation
    const [isBuffering, setIsBuffering] = useState(false);
    const [showTranscript, setShowTranscript] = useState(false);
    const [hoverTime, setHoverTime] = useState(null); // eslint-disable-line no-unused-vars
    const [hoverX, setHoverX] = useState(0); // eslint-disable-line no-unused-vars
    const [activeWordInView, setActiveWordInView] = useState(true);
    const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true);
    const [transcriptData, setTranscriptData] = useState(null);
    const [transcriptLoading, setTranscriptLoading] = useState(false);
    const audioRef = useRef(null);
    const lastScrolledIndex = useRef(-1); // eslint-disable-line no-unused-vars
    const isRestoringRef = useRef(false);
    const restoreTimeoutRef = useRef(null);
    const lastLoadedPodcastIdRef = useRef(null); // Track last loaded podcast ID to prevent loops

    const lastLoadedAudioSrcRef = useRef(null); // Track last loaded audio src to prevent loops
    const retryCountRef = useRef(0); // Track retries for current audio
    const isPlayerActiveRef = useRef(false); // Track if player is logically active (visible/playing) to prevent ghost playback
    const retryTimeoutRef = useRef(null); // Track retry timeout to cancel it on close
    const lastDisplaySecondRef = useRef(-1);
    const transcriptRafRef = useRef(null);
    const justClosedUntilRef = useRef(0);
    const closeInProgressRef = useRef(false);
    const setupAudioTimeoutRef = useRef(null);
    const directLoadTimeoutRef = useRef(null);
    const fallbackPlayTimeoutRef = useRef(null);
    const speedControlRef = useRef(null);
    const lastProgressSavedSecondRef = useRef(-1);
    const isPlayerClosedByMemberRef = useRef(false); // Ref to track if we explicitly closed this session
    const lastServerSyncSecondRef = useRef(-1);

    const currentPodcastRef = useRef(currentPodcast);
    const isPlayingRef = useRef(isPlaying);

    useEffect(() => {
        currentPodcastRef.current = currentPodcast;
    }, [currentPodcast]);

    useEffect(() => {
        isPlayingRef.current = isPlaying;
    }, [isPlaying]);

    const getLiveIsPlaying = () => {
        if (!audioRef.current) return false;
        return !audioRef.current.paused && !audioRef.current.ended;
    };
    
    // Chapter Logic
    const chapters = useMemo(() => {
        return parseChapters(currentPodcast ? currentPodcast.overview : null);
    }, [currentPodcast]);

    const currentChapter = useMemo(() => {
        return getCurrentChapter(chapters, currentTime);
    }, [chapters, currentTime]);
    
    const [volume, setVolume] = useState(() => {
        try {
            const saved = localStorage.getItem(VOLUME_STORAGE_KEY);
            if (saved === null) return 1;
            const parsed = parseFloat(saved);
            if (!Number.isFinite(parsed)) return 1;
            return Math.min(1, Math.max(0, parsed));
        } catch (_) {
            return 1;
        }
    });
    const lastNonZeroVolumeRef = useRef(volume > 0 ? volume : 1);
    useEffect(() => {
        if (volume > 0) {
            lastNonZeroVolumeRef.current = volume;
        }
    }, [volume]);
    const [playbackRate, setPlaybackRate] = useState(1);
    const [showSpeedPopup, setShowSpeedPopup] = useState(false);
    const SPEED_OPTIONS = [0.8, 1, 1.25, 1.5, 1.8, 2];

    const handleVolumeChange = useCallback((nextVolume) => {
        const safeVolume = Math.min(1, Math.max(0, Number(nextVolume) || 0));
        setVolume(safeVolume);

        if (audioRef.current) {
            audioRef.current.volume = safeVolume;
            audioRef.current.muted = safeVolume === 0;
        }

        try {
            localStorage.setItem(VOLUME_STORAGE_KEY, String(safeVolume));
        } catch (_) {
            // ignore storage errors
        }
    }, []);

    // Keep media element volume aligned with slider state.
    useEffect(() => {
        if (!audioRef.current) return;
        audioRef.current.volume = volume;
        audioRef.current.muted = volume === 0;
    }, [volume]);

    // Sync slider from real media volume changes (e.g. browser media controls).
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const syncFromAudio = () => {
            const next = audio.muted ? 0 : audio.volume;
            setVolume(prev => (Math.abs(prev - next) > 0.001 ? next : prev));
            try {
                localStorage.setItem(VOLUME_STORAGE_KEY, String(next));
            } catch (_) {
                // ignore storage errors
            }
        };

        audio.addEventListener('volumechange', syncFromAudio);
        return () => audio.removeEventListener('volumechange', syncFromAudio);
    }, [audioPlayerVisible, currentPodcast]);

    useEffect(() => {
        if (audioRef.current) {
            audioRef.current.playbackRate = playbackRate;
        }
    }, [playbackRate, audioPlayerVisible, currentPodcast]);

    useEffect(() => {
        if (!showSpeedPopup) return;
        const handleOutside = (event) => {
            if (speedControlRef.current && !speedControlRef.current.contains(event.target)) {
                setShowSpeedPopup(false);
            }
        };
        document.addEventListener('mousedown', handleOutside);
        return () => document.removeEventListener('mousedown', handleOutside);
    }, [showSpeedPopup]);

    useEffect(() => {
        void hydratePodcastProgressFromServer();
    }, []);
    
    // Tape Progress Bar Refs
    const [isDragging, setIsDragging] = useState(false);
    const [dragTime, setDragTime] = useState(null);
    const dragTimeRef = useRef(0);
    const dragStartXRef = useRef(0);
    const dragStartTimeRef = useRef(0);
    const lastDragXRef = useRef(0);
    const velocityRef = useRef(0);
    const lastMoveTimeRef = useRef(0);
    const momentumScrollRef = useRef(null);
    const tapeContainerRef = useRef(null);

    const handleTapeInteraction = useCallback((clientX) => {
        if (!tapeContainerRef.current) return;
        const rect = tapeContainerRef.current.getBoundingClientRect();
        const centerX = rect.width / 2;
        const diffX = clientX - rect.left - centerX;
        
        // Convert X offset to seconds
        const diffSeconds = diffX / PIXELS_PER_SECOND;
        
        // In this scroller, clicking RIGHT of center means going FORWARD
        const baseTime = isDragging ? dragStartTimeRef.current : (audioRef.current?.currentTime || 0);
        let newTime = baseTime + diffSeconds;
        
        if (isDragging) {
            // When dragging, we track relative to where we started
            const dragDiffX = clientX - dragStartXRef.current;
            const dragDiffSeconds = -dragDiffX / PIXELS_PER_SECOND; // Inverse for natural "pulling" feel
            newTime = dragStartTimeRef.current + dragDiffSeconds;
        }

        return Math.max(0, Math.min(duration, newTime));
    }, [duration, isDragging]);

    const handleMouseDown = (e) => {
        if (momentumScrollRef.current) cancelAnimationFrame(momentumScrollRef.current);
        setIsDragging(true);
        dragStartXRef.current = e.clientX;
        lastDragXRef.current = e.clientX;
        lastMoveTimeRef.current = performance.now();
        velocityRef.current = 0;
        dragStartTimeRef.current = audioRef.current?.currentTime || 0;
        dragTimeRef.current = dragStartTimeRef.current;
        setDragTime(dragStartTimeRef.current);
        
        const scroller = document.getElementById('tape-scroller-inner');
        
        const handleMouseMove = (moveEvent) => {
            const now = performance.now();
            const deltaTime = now - lastMoveTimeRef.current;
            const deltaX = moveEvent.clientX - lastDragXRef.current;
            
            if (deltaTime > 0) {
                velocityRef.current = deltaX / deltaTime;
            }
            
            lastDragXRef.current = moveEvent.clientX;
            lastMoveTimeRef.current = now;

            const dragDiffX = moveEvent.clientX - dragStartXRef.current;
            // 3.0x Sensitivity Multiplier for "Fast" movement
            const dragDiffSeconds = (-dragDiffX * 3.0) / PIXELS_PER_SECOND;
            const newTime = Math.max(0, Math.min(duration, dragStartTimeRef.current + dragDiffSeconds));
            dragTimeRef.current = newTime;
            setDragTime(newTime);
            
            if (scroller) {
                scroller.style.transition = 'none';
                const currentPlace = newTime * PIXELS_PER_SECOND;
                scroller.style.transform = `translateX(-${currentPlace}px)`;
            }
        };
        
        const handleMouseUp = () => {
            setIsDragging(false);
            if (Math.abs(velocityRef.current) > 0.4) {
                startMomentum(velocityRef.current);
            } else {
                if (audioRef.current) audioRef.current.currentTime = dragTimeRef.current;
                setDragTime(null);
            }
            
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
        
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    };

    const handleTouchStart = (e) => {
        if (momentumScrollRef.current) cancelAnimationFrame(momentumScrollRef.current);
        const touch = e.touches[0];
        setIsDragging(true);
        dragStartXRef.current = touch.clientX;
        lastDragXRef.current = touch.clientX;
        lastMoveTimeRef.current = performance.now();
        velocityRef.current = 0;
        dragStartTimeRef.current = audioRef.current?.currentTime || 0;
        dragTimeRef.current = dragStartTimeRef.current;
        setDragTime(dragStartTimeRef.current);
        
        const scroller = document.getElementById('tape-scroller-inner');
        
        const handleTouchMove = (moveEvent) => {
            const moveTouch = moveEvent.touches[0];
            const now = performance.now();
            const deltaTime = now - lastMoveTimeRef.current;
            const deltaX = moveTouch.clientX - lastDragXRef.current;
            
            if (deltaTime > 0) {
                velocityRef.current = deltaX / deltaTime;
            }
            
            lastDragXRef.current = moveTouch.clientX;
            lastMoveTimeRef.current = now;

            const dragDiffX = moveTouch.clientX - dragStartXRef.current;
            const dragDiffSeconds = (-dragDiffX * 3.0) / PIXELS_PER_SECOND;
            const newTime = Math.max(0, Math.min(duration, dragStartTimeRef.current + dragDiffSeconds));
            dragTimeRef.current = newTime;
            setDragTime(newTime);

            if (scroller) {
                scroller.style.transition = 'none';
                const currentPlace = newTime * PIXELS_PER_SECOND;
                scroller.style.transform = `translateX(-${currentPlace}px)`;
            }
        };
        
        const handleTouchEnd = () => {
            setIsDragging(false);
            if (Math.abs(velocityRef.current) > 0.4) {
                startMomentum(velocityRef.current);
            } else {
                if (audioRef.current) audioRef.current.currentTime = dragTimeRef.current;
                setDragTime(null);
            }
            
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
        
        document.addEventListener('touchmove', handleTouchMove, { passive: false });
        document.addEventListener('touchend', handleTouchEnd);
    };

    const startMomentum = (initialVelocity) => {
        let vel = initialVelocity;
        const friction = 0.95;
        const scroller = document.getElementById('tape-scroller-inner');
        
        const animate = () => {
            vel *= friction;
            if (Math.abs(vel) < 0.05) {
                if (audioRef.current) audioRef.current.currentTime = dragTimeRef.current;
                setDragTime(null);
                return;
            }
            
            // Increased momentum speed boost
            const timeDiff = -vel * 40 / PIXELS_PER_SECOND; 
            const nextTime = Math.max(0, Math.min(duration, dragTimeRef.current + timeDiff));
            dragTimeRef.current = nextTime;
            setDragTime(nextTime);
            
            if (scroller) {
                const currentPlace = nextTime * PIXELS_PER_SECOND;
                scroller.style.transform = `translateX(-${currentPlace}px)`;
            }
            
            momentumScrollRef.current = requestAnimationFrame(animate);
        };
        
        momentumScrollRef.current = requestAnimationFrame(animate);
    };

    const isDraggingRef = useRef(false); // eslint-disable-line no-unused-vars

    // Calculate pixels per second for the tape (keep for calculations outside component)
    const PIXELS_PER_SECOND = 6; 
    
    /* Removed inline animation logic - moved to TapeScroller component */
    
    // Function to correct thumbnail URL based on audio file pattern
    const correctThumbnailUrl = (podcast) => {
        if (!podcast || !podcast.audioFile) {
            return (podcast && podcast.thumbnail) || null;
        }
        
        try {
            // Extract podcast number from audio file URL
            // Pattern: .../Podcast-{number}/Podcast+{number}.mp3 or .../episode-{number}/Podcast+{number}.mp3
            const audioMatch = podcast.audioFile.match(/\/(?:Podcast-|episode-)(\d+)\//);
            const podcastNumber = audioMatch ? audioMatch[1] : null;
            
            if (podcastNumber) {
                // Construct correct thumbnail URL
                const baseUrl = 'https://podcast-audio-episodes.s3.eu-north-1.amazonaws.com';
                const correctPath = `episode-${podcastNumber}/Podcast+${podcastNumber}+Thumbnail.jpg`;
                const correctUrl = `${baseUrl}/${correctPath}`;
                
                console.log('[GlobalAudioPlayer] 🔧 Correcting thumbnail URL:');
                console.log('[GlobalAudioPlayer] Original thumbnail:', podcast.thumbnail);
                console.log('[GlobalAudioPlayer] Audio file:', podcast.audioFile);
                console.log('[GlobalAudioPlayer] Extracted podcast number:', podcastNumber);
                console.log('[GlobalAudioPlayer] ✅ Corrected thumbnail URL:', correctUrl);
                
                return correctUrl;
            }
        } catch (error) {
            console.error('[GlobalAudioPlayer] Error correcting thumbnail URL:', error);
        }
        
        // Fallback to original thumbnail if correction fails
        return (podcast && podcast.thumbnail) || null;
    };
    
    // Update corrected thumbnail URL when currentPodcast changes - use useMemo to prevent loops
    useEffect(() => {
        if (currentPodcast) {
            const corrected = correctThumbnailUrl(currentPodcast);
            setCorrectedThumbnailUrl(corrected);
        } else {
            setCorrectedThumbnailUrl(null);
        }
    }, [currentPodcast ? (currentPodcast._id || currentPodcast.slug) : null]); // eslint-disable-line react-hooks/exhaustive-deps

    // Reset transcript when podcast changes
    useEffect(() => {
        setTranscriptData(null);
        setTranscriptLoading(false);
    }, [currentPodcast ? (currentPodcast._id || currentPodcast.slug) : null]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchTranscript = async (slug) => {
        if (!slug || transcriptLoading) return;
        setTranscriptLoading(true);
        try {
            const response = await robustFetch(API_ENDPOINTS.PODCAST.TRANSCRIPT(slug));
            const rawText = await response.text();
            const trimmed = rawText ? rawText.trim() : '';
            if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
                setTranscriptData({ transcript: [] });
                return;
            }
            let data;
            try { data = JSON.parse(trimmed); } catch (e) { setTranscriptData({ transcript: [] }); return; }
            setTranscriptData(data);
        } catch (err) {
            console.error('[GlobalAudioPlayer] Error fetching transcript:', err);
            setTranscriptData({ transcript: [] });
        } finally {
            setTranscriptLoading(false);
        }
    };

    // Fetch all podcasts on mount for navigation
    useEffect(() => {
        const fetchPodcasts = async () => {
            try {
                const response = await robustFetch(API_ENDPOINTS.PODCAST.ALL);
                if (response.ok) {
                    const data = await response.json();
                    setAllPodcasts(data);
                }
            } catch (error) {
                console.error('[GlobalAudioPlayer] Error fetching podcasts:', error);
            }
        };
        fetchPodcasts();
    }, []);

    // eslint-disable-next-line no-unused-vars
    const handleNextEpisode = () => {
        if (!currentPodcast || allPodcasts.length === 0) return;
        
        const currentIndex = allPodcasts.findIndex(p => p._id === currentPodcast._id || p.slug === currentPodcast.slug);
        if (currentIndex === -1) return;

        // Logic: Usually newer episodes are first. 
        // "Next" usually means the next one in the list (older? or newer? depends on sort).
        // Let's assume standard playlist behavior: Next index.
        const nextIndex = currentIndex + 1;
        if (nextIndex < allPodcasts.length) {
            playPodcastGlobal(allPodcasts[nextIndex]);
        }
    };

    // eslint-disable-next-line no-unused-vars
    const handlePreviousEpisode = () => {
        if (!currentPodcast || allPodcasts.length === 0) return;
        
        const currentIndex = allPodcasts.findIndex(p => p._id === currentPodcast._id || p.slug === currentPodcast.slug);
        if (currentIndex === -1) return;

        const prevIndex = currentIndex - 1;
        if (prevIndex >= 0) {
            playPodcastGlobal(allPodcasts[prevIndex]);
        }
    };

    // Function to restore audio playback
    const restoreAudioPlayback = useCallback((state) => {
        // Prevent multiple simultaneous restore attempts
        if (isRestoringRef.current || !isPlayerActiveRef.current) {
            return;
        }
        
        if (!audioRef.current) {
            // Audio element not ready yet, try again
            setTimeout(() => restoreAudioPlayback(state), 100);
            return;
        }
        
        isRestoringRef.current = true;
        
        // Clear any pending restore timeout
        if (restoreTimeoutRef.current) {
            clearTimeout(restoreTimeoutRef.current);
        }
        
        // Debounce restore attempts
        restoreTimeoutRef.current = setTimeout(() => {
            try {
                // Check if audioRef is still available and player is active
                if (!audioRef.current || !isPlayerActiveRef.current || isPlayerClosedByMemberRef.current) {
                    isRestoringRef.current = false;
                    return;
                }
                
                const currentSrc = audioRef.current.src || '';
                const newSrc = state.currentPodcast.audioFile || '';
                const srcMatches = currentSrc && (
                    currentSrc.includes(newSrc.split('/').pop()) || 
                    newSrc.includes(currentSrc.split('/').pop())
                );
                
                if (!srcMatches) {
                    // Need to load new audio
                    if (audioRef.current) {
                        audioRef.current.src = state.currentPodcast.audioFile;
                        audioRef.current.currentTime = state.currentTime || 0;
                        audioRef.current.load();
                        
                        audioRef.current.onloadedmetadata = () => {
                            if (audioRef.current && isPlayerActiveRef.current) {
                                setDuration(audioRef.current.duration);
                                // Set current time AFTER metadata is loaded to ensure it sticks
                                if (state.currentTime) {
                                    audioRef.current.currentTime = state.currentTime;
                                }
                                
                                if (state.isPlaying) {
                                    // Only play if state says it should be playing and audio is ready
                                    audioRef.current.play().catch(err => {
                                        // NotAllowedError is common if browser requires explicit interaction, but user just clicked to navigate
                                        if (err.name !== 'AbortError') {
                                            console.warn('[GlobalAudioPlayer] Auto-resume audio play notice:', err.message);
                                        }
                                        // Keep playing state in UI so user can click play to resume if blocked
                                        if (isPlayerActiveRef.current) setIsPlaying(true);
                                    });
                                }
                            }
                        };
                    }
                } else if (state.isPlaying && audioRef.current.paused) {
                    // Same audio, just resume if it was playing
                    // Check if audio is ready before playing
                    if (audioRef.current && audioRef.current.readyState >= 2) {
                        audioRef.current.currentTime = state.currentTime || 0;
                        audioRef.current.play().catch(err => {
                            // AbortError is expected when play() is interrupted - ignore it
                            if (err.name !== 'AbortError') {
                                console.error('Failed to resume playback:', err);
                            }
                            if (isPlayerActiveRef.current) setIsPlaying(false);
                        });
                    }
                } else if (!state.isPlaying && audioRef.current && !audioRef.current.paused) {
                    // Should be paused but is playing
                    audioRef.current.pause();
                }
            } finally {
                isRestoringRef.current = false;
            }
        }, 100); // 100ms debounce
    }, []);

    // Load state from sessionStorage/cross-subdomain cookie on mount and restore playback
    useEffect(() => {
        const loadAndRestoreState = () => {
            // Check if player was explicitly closed - only prevent auto-restore on mount
            const wasClosedLocal = sessionStorage.getItem(STORAGE_KEY + '_closed');
            const wasClosedCookie = getCrossSubdomainState(STORAGE_KEY + '_closed');
            if (wasClosedLocal === 'true' || wasClosedCookie === 'true') {
                console.log('[GlobalAudioPlayer] Player was previously closed, not restoring on mount');
                return;
            }

            let savedState = sessionStorage.getItem(STORAGE_KEY);
            if (!savedState) {
                savedState = getCrossSubdomainState(STORAGE_KEY);
            }

            if (savedState) {
                try {
                    const state = JSON.parse(savedState);
                    if (state.currentPodcast && state.audioPlayerVisible) {
                        setCurrentPodcast(state.currentPodcast);
                        setAudioPlayerVisible(true);
                        isPlayerActiveRef.current = true; // Mark active
                        setCurrentTime(state.currentTime || 0);

                        // If it was playing when user navigated across subdomains, resume playback immediately
                        const shouldResumePlaying = Boolean(state.isPlaying);
                        setIsPlaying(shouldResumePlaying);

                        setTimeout(() => restoreAudioPlayback({ ...state, isPlaying: shouldResumePlaying }), 200);
                    }
                } catch (e) {
                    console.error('Error loading audio player state:', e);
                }
            }
        };
        
        loadAndRestoreState();
        
        // Listen for page visibility changes (handles tab switching / bfcache navigation)
        const handleVisibilityChange = () => {
            if (!document.hidden && !isRestoringRef.current) {
                // Check if player was explicitly closed
                const wasClosed = sessionStorage.getItem(STORAGE_KEY + '_closed') || getCrossSubdomainState(STORAGE_KEY + '_closed');
                if (wasClosed === 'true') return;

                const savedState = sessionStorage.getItem(STORAGE_KEY) || getCrossSubdomainState(STORAGE_KEY);
                if (savedState) {
                    try {
                        const state = JSON.parse(savedState);
                        if (state.currentPodcast && state.audioPlayerVisible && state.isPlaying) {
                            // Only restore if we don't already have this podcast playing
                            const currentPodcastVal = currentPodcastRef.current;
                            if (!currentPodcastVal || (currentPodcastVal._id !== state.currentPodcast._id && currentPodcastVal.slug !== state.currentPodcast.slug)) {
                                setCurrentPodcast(state.currentPodcast);
                                setAudioPlayerVisible(true);
                            }
                            // Check if audio is actually paused before trying to restore
                            if (audioRef.current && audioRef.current.paused && isPlayingRef.current) {
                                restoreAudioPlayback(state);
                            }
                        }
                    } catch (e) {
                        // Ignore errors
                    }
                }
            }
        };

        // Listen for focus events (handles back button navigation)
        const handleFocus = () => {
            if (!isRestoringRef.current) {
                // Check if player was explicitly closed
                const wasClosed = sessionStorage.getItem(STORAGE_KEY + '_closed') || getCrossSubdomainState(STORAGE_KEY + '_closed');
                if (wasClosed === 'true') return;

                const savedState = sessionStorage.getItem(STORAGE_KEY) || getCrossSubdomainState(STORAGE_KEY);
                if (savedState) {
                    try {
                        const state = JSON.parse(savedState);
                        if (state.currentPodcast && state.audioPlayerVisible && state.isPlaying) {
                            // Only restore if we don't already have this podcast playing
                            const currentPodcastVal = currentPodcastRef.current;
                            if (!currentPodcastVal || (currentPodcastVal._id !== state.currentPodcast._id && currentPodcastVal.slug !== state.currentPodcast.slug)) {
                                setCurrentPodcast(state.currentPodcast);
                                setAudioPlayerVisible(true);
                            }
                            // Check if audio is actually paused before trying to restore
                            if (audioRef.current && audioRef.current.paused && isPlayingRef.current) {
                                setTimeout(() => restoreAudioPlayback(state), 100);
                            }
                        }
                    } catch (e) {
                        // Ignore errors
                    }
                }
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        window.addEventListener('focus', handleFocus);

        // Sync state across tabs
        const handleStorageChange = (e) => {
            if (e.key === STORAGE_KEY + '_closed' && e.newValue === 'true') {
                isPlayerClosedByMemberRef.current = true;
                closeAudioPlayer(null); // Close locally if another tab closed it
            }
        };
        window.addEventListener('storage', handleStorageChange);

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            window.removeEventListener('focus', handleFocus);
            window.removeEventListener('storage', handleStorageChange);
            if (restoreTimeoutRef.current) {
                clearTimeout(restoreTimeoutRef.current);
            }
        };
    }, [restoreAudioPlayback]);

    // Save state to sessionStorage & cross-subdomain cookie (debounced via effect)
    useEffect(() => {
        if (isPlayerClosedByMemberRef.current) return; // DON'T SAVE if we just closed it!

        const liveIsPlaying = getLiveIsPlaying();
        if (currentPodcast && audioPlayerVisible) {
            const state = {
                currentPodcast,
                audioPlayerVisible,
                currentTime,
                duration,
                isPlaying: liveIsPlaying,
                isBuffering,
                timestamp: Date.now()
            };
            const jsonStr = JSON.stringify(state);
            sessionStorage.setItem(STORAGE_KEY, jsonStr);
            setCrossSubdomainState(STORAGE_KEY, jsonStr);
            // Clear closed flag when we save state (meaning player is active)
            sessionStorage.removeItem(STORAGE_KEY + '_closed');
            removeCrossSubdomainState(STORAGE_KEY + '_closed');
        } else if (!audioPlayerVisible) {
            // Only remove if player is not visible
            sessionStorage.removeItem(STORAGE_KEY);
            removeCrossSubdomainState(STORAGE_KEY);
        }
    }, [currentPodcast, audioPlayerVisible, currentTime, duration, isPlaying, isBuffering]); // eslint-disable-line react-hooks/exhaustive-deps

    // Notify other components with live global player state
    useEffect(() => {
        const liveIsPlaying = getLiveIsPlaying();
        window.dispatchEvent(new CustomEvent('audioPlayingChange', {
            detail: {
                isPlaying: liveIsPlaying,
                currentPodcast,
                isBuffering,
                audioPlayerVisible,
                currentTime,
                duration
            }
        }));
    }, [isPlaying, currentPodcast, isBuffering, audioPlayerVisible, currentTime, duration]);

    // Listen for play events from other components
    useEffect(() => {
        const handlePlayPodcast = (event) => {
            if (Date.now() < justClosedUntilRef.current) {
                console.log('[GlobalAudioPlayer] Ignoring playPodcast right after close');
                return;
            }

            const podcast = event.detail;
            console.log('[GlobalAudioPlayer] playPodcast event received:', {
                podcast: podcast ? {
                    _id: podcast._id,
                    slug: podcast.slug,
                    title: podcast.title,
                    audioFile: podcast.audioFile,
                    hasAudioFile: !!podcast.audioFile,
                    audioFileType: typeof podcast.audioFile
                } : null
            });
            
            if (podcast) {
                // Reactivate if it was closed
                isPlayerClosedByMemberRef.current = false;
                
                // Always clear the closed flag FIRST when a new podcast is explicitly played
                sessionStorage.removeItem(STORAGE_KEY + '_closed');
                removeCrossSubdomainState(STORAGE_KEY + '_closed');
                console.log('[GlobalAudioPlayer] Cleared closed flag for new podcast play');
                
                // Validate podcast data
                if (!podcast.audioFile) {
                    console.error('[GlobalAudioPlayer] ERROR: Podcast audioFile is missing:', podcast);
                    alert('Error: Audio file is not available for this podcast');
                    return;
                }
                
                if (typeof podcast.audioFile !== 'string' || podcast.audioFile.trim() === '') {
                    console.error('[GlobalAudioPlayer] ERROR: Podcast audioFile is empty or invalid:', podcast.audioFile);
                    alert('Error: Audio file URL is invalid');
                    return;
                }

                // Check if the URL is valid
                try {
                    new URL(podcast.audioFile);
                } catch (e) {
                    console.error('[GlobalAudioPlayer] ERROR: Invalid URL format:', podcast.audioFile);
                    alert('Error: Audio file URL is malformed');
                    return;
                }
                
                console.log('[GlobalAudioPlayer] Setting current podcast and showing player');
                
                // Reset loaded flags FIRST to force audio to load and play
                lastLoadedPodcastIdRef.current = null;
                lastLoadedAudioSrcRef.current = null;
                retryCountRef.current = 0; // Reset retry count
                console.log('[GlobalAudioPlayer] Reset loaded flags - forcing fresh audio load');
                
                // Calculate corrected thumbnail URL immediately
                const correctedThumb = correctThumbnailUrl(podcast);
                console.log('[GlobalAudioPlayer] Corrected thumbnail calculated:', correctedThumb);
                
                // Set state immediately to show player
                setCurrentPodcast(podcast);
                setAudioPlayerVisible(true);
                isPlayerActiveRef.current = true; // Mark active
                setCorrectedThumbnailUrl(correctedThumb); // Set corrected thumbnail immediately
                setIsPlaying(false); // Reset playing state to force fresh start
                
                // Load audio immediately - simplified approach
                directLoadTimeoutRef.current = setTimeout(() => {
                    if (!isPlayerActiveRef.current) {
                        console.log('[GlobalAudioPlayer] Direct load canceled - player inactive');
                        return;
                    }

                    if (!audioRef.current || !podcast.audioFile) {
                        console.error('[GlobalAudioPlayer] Cannot load audio - audioRef:', !!audioRef.current, 'audioFile:', podcast.audioFile);
                        return;
                    }
                    
                    console.log('[GlobalAudioPlayer] Direct audio load - setting src:', podcast.audioFile);
                    const audio = audioRef.current;
                    
                    // Clear any existing handlers
                    audio.oncanplay = null;
                    audio.oncanplaythrough = null;
                    audio.onloadedmetadata = null;
                    audio.onerror = null;
                    audio.onwaiting = null;
                    audio.onplaying = null;
                    
                    // Set up audio
                    audio.pause();
                    audio.src = podcast.audioFile;
                    audio.currentTime = podcast.startTime || 0;
                    audio.load();
                    
                    // Play when ready
                    const playWhenReady = () => {
                        if (audioRef.current && audioRef.current.readyState >= 2) {
                            // STRICT CHECK: Only play if player is still active
                            if (!isPlayerActiveRef.current) {
                                console.log('[GlobalAudioPlayer] Player inactive, skipping playWhenReady');
                                return;
                            }
                            
                            audioRef.current.play().then(() => {
                                console.log('[GlobalAudioPlayer] ✅ DIRECT PLAYBACK STARTED');
                                if (isPlayerActiveRef.current) setIsPlaying(true);
                            }).catch((error) => {
                                console.error('[GlobalAudioPlayer] ❌ Direct play failed:', error);
                                if (error.name !== 'AbortError') {
                                    console.error('[GlobalAudioPlayer] Play error details:', error);
                                }
                            });
                        }
                    };
                    
                    audio.oncanplay = () => {
                        console.log('[GlobalAudioPlayer] Direct load - Audio can play');
                        playWhenReady();
                    };
                    
                    audio.oncanplaythrough = () => {
                        console.log('[GlobalAudioPlayer] Direct load - Audio can play through');
                        if (audioRef.current && audioRef.current.paused) {
                            playWhenReady();
                        }
                    };
                    
                    audio.onloadedmetadata = () => {
                        if (audioRef.current && audioRef.current.duration) {
                            setDuration(audioRef.current.duration);
                        }
                    };

                    audio.onwaiting = () => {
                         console.log('[GlobalAudioPlayer] Audio buffering/waiting...');
                         if (isPlayerActiveRef.current) setIsBuffering(true);
                    };

                    audio.onplaying = () => {
                         console.log('[GlobalAudioPlayer] Audio playing...');
                         if (isPlayerActiveRef.current) setIsBuffering(false);
                    };
                    
                    audio.onerror = (e) => {
                        console.error('[GlobalAudioPlayer] ❌ Direct audio error:', e);
                        console.error('[GlobalAudioPlayer] Error code:', audio.error && audio.error.code);
                        console.error('[GlobalAudioPlayer] Error message:', audio.error && audio.error.message);
                        console.error('[GlobalAudioPlayer] Audio SRC that failed:', audio.src);
                        
                        // Attempt to recover if it's a specific error
                        if (audio.error && audio.error.code === 4) {
                             console.error('[GlobalAudioPlayer] MEDIA_ELEMENT_ERROR: Format not supported or source unavailable.');
                             
                             // Retry logic for 403/404 errors (often due to URL encoding issues or wrong path)
                             if (retryCountRef.current < 4) {
                                 retryCountRef.current++;
                                 console.log(`[GlobalAudioPlayer] 🔄 Retry attempt ${retryCountRef.current} for audio...`);
                                 
                                 let nextSrc = audio.src;
                                 
                                 // Strategy 1: Try replacing + with %20 (space)
                                 if (retryCountRef.current === 1 && nextSrc.includes('+')) {
                                     nextSrc = nextSrc.replace(/\+/g, '%20');
                                     console.log('[GlobalAudioPlayer] Retrying with + replaced by %20:', nextSrc);
                                 } 
                                 // Strategy 2: Try replacing Podcast-N with episode-N (common folder naming mismatch)
                                 else if (retryCountRef.current === 2 && nextSrc.includes('/Podcast-')) {
                                     // Reset to original + format but change folder
                                     // We need to reconstruct from the original if possible, but here we just try to fix the current src
                                     // If we are at retry 2, we might have already replaced + with %20. 
                                     // Let's try to fix the folder path first.
                                     
                                     // Get the original URL from the current podcast object if available, otherwise use current src
                                     const originalUrl = currentPodcast && currentPodcast.audioFile ? currentPodcast.audioFile : nextSrc;
                                     
                                     if (originalUrl.includes('/Podcast-')) {
                                         nextSrc = originalUrl.replace(/\/Podcast-(\d+)\//, '/episode-$1/');
                                         console.log('[GlobalAudioPlayer] Retrying with folder correction (Podcast- -> episode-):', nextSrc);
                                     }
                                 }
                                 // Strategy 3: Try folder correction AND space encoding
                                 else if (retryCountRef.current === 3) {
                                     const originalUrl = currentPodcast && currentPodcast.audioFile ? currentPodcast.audioFile : nextSrc;
                                     if (originalUrl.includes('/Podcast-')) {
                                          let fixedPath = originalUrl.replace(/\/Podcast-(\d+)\//, '/episode-$1/');
                                          nextSrc = fixedPath.replace(/\+/g, '%20');
                                          console.log('[GlobalAudioPlayer] Retrying with folder correction AND space encoding:', nextSrc);
                                     }
                                 }
                                 
                                 if (nextSrc !== audio.src) {
                                     // Clear any existing retry timeout
                                     if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current);
                                     
                                     retryTimeoutRef.current = setTimeout(() => {
                                         if (audioRef.current && isPlayerActiveRef.current) {
                                             audioRef.current.src = nextSrc;
                                             audioRef.current.load();
                                             audioRef.current.oncanplay = () => {
                                                 audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
                                             };
                                         }
                                     }, 500);
                                     return;
                                 }
                             }
                        }
                    };
                    
                    // Fallback: try to play after a delay
                    fallbackPlayTimeoutRef.current = setTimeout(() => {
                        if (audioRef.current && audioRef.current.readyState >= 2 && audioRef.current.paused && isPlayerActiveRef.current) {
                            console.log('[GlobalAudioPlayer] Fallback: attempting play after delay');
                            playWhenReady();
                        }
                    }, 1000);
                }, 100);
            } else {
                console.error('[GlobalAudioPlayer] ERROR: Podcast data is null or undefined in event.detail');
            }
        };

        const handlePausePodcast = () => {
            // Cancel any pending restore attempts
            if (restoreTimeoutRef.current) {
                clearTimeout(restoreTimeoutRef.current);
                restoreTimeoutRef.current = null;
            }
            isRestoringRef.current = false;
            
            if (audioRef.current && !audioRef.current.paused) {
                audioRef.current.pause();
                setIsPlaying(false);
            }
        };

        console.log('[GlobalAudioPlayer] Registering playPodcast event listener');
        window.addEventListener('playPodcast', handlePlayPodcast);
        window.addEventListener('pausePodcast', handlePausePodcast);
        
        return () => {
            console.log('[GlobalAudioPlayer] Unregistering playPodcast event listeners');
            window.removeEventListener('playPodcast', handlePlayPodcast);
            window.removeEventListener('pausePodcast', handlePausePodcast);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Helper to get CSS variable value
    // eslint-disable-next-line no-unused-vars
    const getCssVariable = (variable) => {
        return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
    };

    // Tape Progress Bar Logic
    // eslint-disable-next-line no-unused-vars
    const formatTapeTime = (seconds) => {
        const m = Math.floor(seconds / 60);
        const s = Math.floor(seconds % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    // We can pre-calculate markers if duration is known
    const tapeMarkers = useMemo(() => {
        if (!duration) return [];
        const markers = [];
        // Add a marker every 30 seconds
        for (let i = 0; i <= duration; i += 30) {
            markers.push(i);
        }
        return markers;
    }, [duration]);
 



    // Load and play audio when podcast changes
    useEffect(() => {
        console.log('[GlobalAudioPlayer] useEffect check:', {
            hasPodcast: !!currentPodcast,
            visible: audioPlayerVisible,
            hasAudioFile: !!(currentPodcast && currentPodcast.audioFile)
        });
        
        if (!currentPodcast || !audioPlayerVisible || !currentPodcast.audioFile) {
            console.log('[GlobalAudioPlayer] useEffect skipping - missing requirements');
            return;
        }
        
        const newSrc = currentPodcast.audioFile;
        const podcastId = currentPodcast._id || currentPodcast.slug;
        
        console.log('[GlobalAudioPlayer] useEffect triggered - loading audio for:', currentPodcast.title);
        console.log('[GlobalAudioPlayer] Audio file URL:', newSrc);
        console.log('[GlobalAudioPlayer] Podcast ID:', podcastId);
        
        // Prevent infinite loops - but allow reload if audio failed
        if (lastLoadedPodcastIdRef.current === podcastId && lastLoadedAudioSrcRef.current === newSrc) {
            console.log('[GlobalAudioPlayer] Audio already loaded - checking playback state');
            directLoadTimeoutRef.current = setTimeout(() => {
                if (audioRef.current && isPlayerActiveRef.current) {
                    const audio = audioRef.current;
                    // If audio has an error, reload it
                    if (audio.error && audio.error.code !== 0) {
                        console.log('[GlobalAudioPlayer] Audio had error, reloading...');
                        lastLoadedPodcastIdRef.current = null;
                        lastLoadedAudioSrcRef.current = null;
                        return; // Will trigger reload in next render
                    }
                    // If audio is ready but paused, play it
                    if (audio.paused && audio.readyState >= 2) {
                        console.log('[GlobalAudioPlayer] Audio ready but paused - playing...');
                        audio.play().then(() => setIsPlaying(true)).catch(() => {});
                    } else if (!audio.src || audio.src !== newSrc) {
                        console.log('[GlobalAudioPlayer] Audio src mismatch, reloading...');
                        audio.src = newSrc;
                        audio.load();
                        audio.oncanplay = () => {
                            audio.play().then(() => setIsPlaying(true)).catch(() => {});
                        };
                    }
                }
            }, 100);
            return;
        }
        
        // Mark as loaded immediately to prevent loops
        lastLoadedPodcastIdRef.current = podcastId;
        lastLoadedAudioSrcRef.current = newSrc;
        
        // Wait for audio element to be available
        const setupAudio = () => {
            if (!isPlayerActiveRef.current) {
                console.log('[GlobalAudioPlayer] setupAudio skipped - player inactive');
                return;
            }

            if (!audioRef.current) {
                console.warn('[GlobalAudioPlayer] Audio ref not ready, retrying...');
                setupAudioTimeoutRef.current = setTimeout(setupAudio, 100);
                return;
            }
            
            const audio = audioRef.current;
            
            // Always load fresh audio when podcast changes
            console.log('[GlobalAudioPlayer] Loading audio file:', newSrc);
            
            // Stop current playback and load new audio
            audio.pause();
            audio.src = newSrc;
            audio.currentTime = 0;
            audio.load();
            
            console.log('[GlobalAudioPlayer] Audio load() called, waiting for ready...');
            
            // Clear previous event handlers
            audio.onloadedmetadata = null;
            audio.oncanplay = null;
            audio.onerror = null;
            
            audio.onloadedmetadata = () => {
                if (!audioRef.current) return;
                const duration = audioRef.current.duration;
                console.log('[GlobalAudioPlayer] Metadata loaded, duration:', duration);
                if (duration && !isNaN(duration)) {
                    setDuration(duration);
                }
            };
            
            // Use both oncanplay and oncanplaythrough for better compatibility
            const attemptPlay = () => {
                if (!audioRef.current) return;
                
                // STRICT CHECK: Only play if player is still active
                if (!isPlayerActiveRef.current) {
                    console.log('[GlobalAudioPlayer] Player inactive, skipping attemptPlay');
                    return;
                }
                
                console.log('[GlobalAudioPlayer] Attempting to play audio...');
                audioRef.current.play().then(() => {
                    console.log('[GlobalAudioPlayer] ✅ PLAYBACK STARTED SUCCESSFULLY');
                    if (isPlayerActiveRef.current) setIsPlaying(true);
                }).catch((error) => {
                    console.error('[GlobalAudioPlayer] ❌ Play failed:', error);
                    if (error.name !== 'AbortError') {
                        console.error('[GlobalAudioPlayer] Error name:', error.name);
                        console.error('[GlobalAudioPlayer] Error message:', error.message);
                        // Don't set isPlaying to false immediately - user might click play button
                    }
                });
            };
            
            audio.oncanplay = () => {
                console.log('[GlobalAudioPlayer] Audio can play event fired');
                attemptPlay();
            };
            
            audio.oncanplaythrough = () => {
                console.log('[GlobalAudioPlayer] Audio can play through event fired');
                // Try play again if not already playing
                if (audioRef.current && audioRef.current.paused && !isPlaying) {
                    attemptPlay();
                }
            };
            
            audio.onerror = (e) => {
                console.error('[GlobalAudioPlayer] ❌ Audio error:', e);
                console.error('[GlobalAudioPlayer] Audio error code:', audio.error && audio.error.code);
                console.error('[GlobalAudioPlayer] Audio error message:', audio.error && audio.error.message);
                console.error('[GlobalAudioPlayer] Audio src:', audio.src);
                console.error('[GlobalAudioPlayer] Audio networkState:', audio.networkState);
                console.error('[GlobalAudioPlayer] Audio readyState:', audio.readyState);
                
                // Error code 4 = MEDIA_ELEMENT_ERROR (network/format error)
                // This usually means 403/404 or network issue
                if (audio.error && audio.error.code === 4) {
                    console.error('[GlobalAudioPlayer] ❌ CRITICAL: Cannot access audio file. This is likely an AWS S3 permissions/CORS issue.');
                    console.error('[GlobalAudioPlayer] Please check:');
                    console.error('[GlobalAudioPlayer] 1. S3 bucket CORS configuration');
                    console.error('[GlobalAudioPlayer] 2. S3 bucket public access settings');
                    console.error('[GlobalAudioPlayer] 3. File permissions and path');
                    
                    // Retry logic for 403/404 errors
                    if (retryCountRef.current < 4) {
                        retryCountRef.current++;
                        console.log(`[GlobalAudioPlayer] 🔄 Retry attempt ${retryCountRef.current} for audio...`);
                        
                        let nextSrc = audio.src;
                        
                        // Strategy 1: Try replacing + with %20 (space)
                        if (retryCountRef.current === 1 && nextSrc.includes('+')) {
                            nextSrc = nextSrc.replace(/\+/g, '%20');
                            console.log('[GlobalAudioPlayer] Retrying with + replaced by %20:', nextSrc);
                        } 
                        // Strategy 2: Try replacing Podcast-N with episode-N (common folder naming mismatch)
                        else if (retryCountRef.current === 2 && nextSrc.includes('/Podcast-')) {
                            const originalUrl = currentPodcast && currentPodcast.audioFile ? currentPodcast.audioFile : nextSrc;
                            if (originalUrl.includes('/Podcast-')) {
                                nextSrc = originalUrl.replace(/\/Podcast-(\d+)\//, '/episode-$1/');
                                console.log('[GlobalAudioPlayer] Retrying with folder correction (Podcast- -> episode-):', nextSrc);
                            }
                        }
                        // Strategy 3: Try folder correction AND space encoding
                        else if (retryCountRef.current === 3) {
                            const originalUrl = currentPodcast && currentPodcast.audioFile ? currentPodcast.audioFile : nextSrc;
                            if (originalUrl.includes('/Podcast-')) {
                                    let fixedPath = originalUrl.replace(/\/Podcast-(\d+)\//, '/episode-$1/');
                                    nextSrc = fixedPath.replace(/\+/g, '%20');
                                    console.log('[GlobalAudioPlayer] Retrying with folder correction AND space encoding:', nextSrc);
                            }
                        }
                        
                        if (nextSrc !== audio.src) {
                            retryTimeoutRef.current = setTimeout(() => {
                                if (audioRef.current && isPlayerActiveRef.current) {
                                    audioRef.current.src = nextSrc;
                                    audioRef.current.load();
                                    // Re-attach handlers for the new load
                                    audioRef.current.oncanplay = () => {
                                        console.log('[GlobalAudioPlayer] Retry successful - playing');
                                        if (audioRef.current && isPlayerActiveRef.current) {
                                            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
                                        }
                                        
                                    };
                                }
                            }, 500);
                            return;
                        }
                    }
                }
                
                setIsPlaying(false);
            };
            
            // Add a stall listener
            audio.onstalled = () => {
                console.warn('[GlobalAudioPlayer] Audio playback stalled (data not available)');
            };

            // Add a waiting listener
            audio.onwaiting = () => {
                console.log('[GlobalAudioPlayer] Audio waiting for data...');
            };
            
            // Also try to play after a delay in case events don't fire
            fallbackPlayTimeoutRef.current = setTimeout(() => {
                if (audioRef.current && isPlayerActiveRef.current) {
                    const audio = audioRef.current;
                    if (audio.readyState >= 2 && audio.paused && !audio.error) {
                        console.log('[GlobalAudioPlayer] Fallback: Attempting play after delay, readyState:', audio.readyState);
                        attemptPlay();
                    } else if (audio.error) {
                        console.error('[GlobalAudioPlayer] Cannot play - audio has error:', audio.error.code);
                    }
                }
            }, 800);
            
        };
        
        setupAudioTimeoutRef.current = setTimeout(setupAudio, 150);
    }, [currentPodcast ? (currentPodcast._id || currentPodcast.slug) : null, audioPlayerVisible]); // eslint-disable-line react-hooks/exhaustive-deps

    const togglePlayPause = async () => {
        console.log('[GlobalAudioPlayer] togglePlayPause called');
        
        if (!currentPodcast || !currentPodcast.audioFile) {
            console.error('[GlobalAudioPlayer] No podcast or audioFile available');
            return;
        }
        
        if (!audioRef.current) {
            console.error('[GlobalAudioPlayer] Audio ref is null');
            return;
        }
        
        // Cancel any pending restore attempts
        if (restoreTimeoutRef.current) {
            clearTimeout(restoreTimeoutRef.current);
            restoreTimeoutRef.current = null;
        }
        isRestoringRef.current = false;
        
        try {
            const audio = audioRef.current;
            const audioFile = currentPodcast.audioFile;
            
            // Always ensure audio source is set
            if (!audio.src || !audio.src.includes(audioFile.split('/').pop())) {
                console.log('[GlobalAudioPlayer] Setting audio src in togglePlayPause:', audioFile);
                audio.src = audioFile;
                audio.load();
                
                // Wait for metadata
                await new Promise((resolve, reject) => {
                    if (!audioRef.current) {
                        reject(new Error('Audio ref lost'));
                        return;
                    }
                    
                    const timeout = setTimeout(() => {
                        reject(new Error('Audio metadata timeout'));
                    }, 5000);
                    
                    audioRef.current.onloadedmetadata = () => {
                        clearTimeout(timeout);
                        if (audioRef.current) {
                            setDuration(audioRef.current.duration);
                        }
                        resolve();
                    };
                    
                    audioRef.current.onerror = () => {
                        clearTimeout(timeout);
                        reject(new Error('Audio load error'));
                    };
                });
            }
            
            // Toggle play/pause
            if (audio.paused) {
                console.log('[GlobalAudioPlayer] Playing audio...');
                await audio.play();
                setIsPlaying(true);
                console.log('[GlobalAudioPlayer] ✅ Audio playing');
            } else {
                console.log('[GlobalAudioPlayer] Pausing audio...');
                audio.pause();
                setIsPlaying(false);
            }
        } catch (error) {
            console.error('[GlobalAudioPlayer] Error in togglePlayPause:', error);
            if (error.name !== 'AbortError') {
                console.error('[GlobalAudioPlayer] Error details:', {
                    name: error.name,
                    message: error.message,
                    audioSrc: audioRef.current ? audioRef.current.src : 'no audio ref',
                    audioFile: currentPodcast ? currentPodcast.audioFile : 'no podcast'
                });
            }
            setIsPlaying(false);
        }
    };

    const forwardAudio = () => {
        if (audioRef.current) audioRef.current.currentTime += 15;
    };

    const backwardAudio = () => {
        if (audioRef.current) audioRef.current.currentTime -= 15;
    };

    const handleSkipBackward60 = () => {
        if (audioRef.current) audioRef.current.currentTime -= 60;
    };

    const handleSkipForward60 = () => {
        if (audioRef.current) audioRef.current.currentTime += 60;
    };

    const handleTimeUpdate = () => {
        if (!audioRef.current) return;

        const newTime = audioRef.current.currentTime || 0;
        const actuallyPlaying = !audioRef.current.paused && !audioRef.current.ended;

        // Keep button state tied to real media state even if earlier play/pause events are missed.
        setIsPlaying(prev => (prev === actuallyPlaying ? prev : actuallyPlaying));

        // Keep heavy UI updates throttled for performance-sensitive transcript rendering.
        // Fast enough for smooth scrolling (100ms) but protected by paragraph memoization.
        if (Math.abs(newTime - currentTime) > 0.1) {
            setCurrentTime(newTime);
        }

        // Keep human-readable timer strictly on second ticks to prevent flicker.
        const wholeSecond = Math.floor(newTime);
        if (wholeSecond !== lastDisplaySecondRef.current) {
            lastDisplaySecondRef.current = wholeSecond;
            setDisplayTimeSec(wholeSecond);
        }

        if (currentPodcast && wholeSecond !== lastProgressSavedSecondRef.current) {
            lastProgressSavedSecondRef.current = wholeSecond;
            const nextProgress = upsertPodcastProgress(currentPodcast, {
                currentTime: newTime,
                duration,
                isPlaying: actuallyPlaying,
            });

            if (wholeSecond % 5 === 0 && wholeSecond !== lastServerSyncSecondRef.current) {
                lastServerSyncSecondRef.current = wholeSecond;
                void syncPodcastProgressToServer(currentPodcast, nextProgress);
            }
        }
    };

    // Add event listeners for audio state changes
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        const handlePlay = () => {
            setIsPlaying(true);
            setIsBuffering(false);
            setCurrentTime(audio.currentTime || 0);
        };
        
        const handlePause = () => {
            setIsPlaying(false);
            setIsBuffering(false);
            setCurrentTime(audio.currentTime || 0);
        };
        
        const handleEnded = () => {
            setIsPlaying(false);
            setIsBuffering(false);
            setCurrentTime(audio.duration || 0);
            if (currentPodcast && audio.duration) {
                const completedProgress = upsertPodcastProgress(currentPodcast, {
                    currentTime: audio.duration,
                    duration: audio.duration,
                    isPlaying: false,
                });
                void syncPodcastProgressToServer(currentPodcast, completedProgress);
            }
        };

        const handlePlaying = () => {
            setIsPlaying(true);
            setIsBuffering(false);
            setCurrentTime(audio.currentTime || 0);
        };

        const handleWaiting = () => {
            if (!audio.paused) setIsBuffering(true);
        };

        const handleSeeking = () => {
            if (!audio.paused) setIsBuffering(true);
        };

        const handleSeeked = () => {
            setIsBuffering(false);
            setIsPlaying(!audio.paused && !audio.ended);
            const seekTime = audio.currentTime || 0;
            setCurrentTime(seekTime);
            const wholeSecond = Math.floor(seekTime);
            lastDisplaySecondRef.current = wholeSecond;
            setDisplayTimeSec(wholeSecond);
        };
        
        const handleLoadedMetadata = () => {
            setDuration(audio.duration);
            const startTime = audio.currentTime || 0;
            setCurrentTime(startTime);
            const wholeSecond = Math.floor(startTime);
            lastDisplaySecondRef.current = wholeSecond;
            setDisplayTimeSec(wholeSecond);
        };
        
        // Time update is handled by the onTimeUpdate prop on the audio element

        audio.addEventListener('play', handlePlay);
        audio.addEventListener('pause', handlePause);
        audio.addEventListener('ended', handleEnded);
        audio.addEventListener('playing', handlePlaying);
        audio.addEventListener('waiting', handleWaiting);
        audio.addEventListener('seeking', handleSeeking);
        audio.addEventListener('seeked', handleSeeked);
        audio.addEventListener('loadedmetadata', handleLoadedMetadata);

        return () => {
            audio.removeEventListener('play', handlePlay);
            audio.removeEventListener('pause', handlePause);
            audio.removeEventListener('ended', handleEnded);
            audio.removeEventListener('playing', handlePlaying);
            audio.removeEventListener('waiting', handleWaiting);
            audio.removeEventListener('seeking', handleSeeking);
            audio.removeEventListener('seeked', handleSeeked);
            audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
        };
    }, [currentPodcast, audioPlayerVisible]);

    const formatTime = (time) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
    };

    const timestampToSeconds = (timestamp) => {
        if (typeof timestamp === 'number') return timestamp;
        if (typeof timestamp !== 'string' || !timestamp) return 0;
        
        const cleanTimestamp = timestamp.trim();
        const parts = cleanTimestamp.split(':').map(part => {
            const num = parseInt(part.trim(), 10);
            return isNaN(num) ? 0 : num;
        });
        
        if (parts.length === 3) {
            const hours = parts[0] || 0;
            const minutes = parts[1] || 0;
            const seconds = parts[2] || 0;
            return hours * 3600 + minutes * 60 + seconds;
        } else if (parts.length === 2) {
            const minutes = parts[0] || 0;
            const seconds = parts[1] || 0;
            return minutes * 60 + seconds;
        }
        return 0;
    };

    const getTranscriptWordStart = useCallback((word) => {
        if (!word) return null;

        let raw = null;
        if (word.start !== undefined && word.start !== null) raw = word.start;
        else if (word.startTime !== undefined && word.startTime !== null) raw = word.startTime;
        else if (word.timestamp !== undefined && word.timestamp !== null) raw = word.timestamp;

        if (raw === null) return null;

        let sec = 0;
        if (typeof raw === 'string') {
            sec = raw.includes(':') ? timestampToSeconds(raw) : parseFloat(raw);
        } else {
            sec = Number(raw);
        }

        if (!Number.isFinite(sec)) return null;

        // Heuristic: If timestamp is extremely high (e.g., > 3.6e6), it's likely milliseconds. 
        // 100,000s is ~27 hours, safer than 10,000s (~2.7 hours).
        if (sec > 100000) sec = sec / 1000;

        return Math.max(0, sec);
    }, []);

    // Keep transcript timing live while audio is playing, independent from browser timeupdate cadence.
    useEffect(() => {
        if (!showTranscript || !isPlaying) {
            if (transcriptRafRef.current) {
                cancelAnimationFrame(transcriptRafRef.current);
                transcriptRafRef.current = null;
            }
            return;
        }

        const tick = () => {
            if (audioRef.current) {
                const t = audioRef.current.currentTime || 0;
                setCurrentTime(prev => (Math.abs(prev - t) > 0.01 ? t : prev));
            }
            transcriptRafRef.current = requestAnimationFrame(tick);
        };

        transcriptRafRef.current = requestAnimationFrame(tick);

        return () => {
            if (transcriptRafRef.current) {
                cancelAnimationFrame(transcriptRafRef.current);
                transcriptRafRef.current = null;
            }
        };
    }, [showTranscript, isPlaying]);

    // Compute current word index for word-level transcript highlighting
    const transcriptWords = useMemo(() => {
        const transcriptArray = transcriptData && transcriptData.transcript;
        if (!transcriptArray || !Array.isArray(transcriptArray) || transcriptArray.length === 0) {
            return { words: [], currentIndex: -1 };
        }

        const transcript = transcriptArray;
        let currentIndex = -1;
        
        const isNewFormat = transcript[0] && transcript[0].word !== undefined;
        
        if (isNewFormat) {
            // High-performance Binary Search to find the current word index
            let low = 0;
            let high = transcript.length - 1;
            
            while (low <= high) {
                const mid = Math.floor((low + high) / 2);
                const wordStart = getTranscriptWordStart(transcript[mid]);
                
                if (wordStart !== null && wordStart <= currentTime) {
                    currentIndex = mid;
                    low = mid + 1;
                } else {
                    high = mid - 1;
                }
            }

            if (currentIndex === -1 && transcript.length > 0) {
                currentIndex = 0;
            }

            return { words: transcript, currentIndex, isNewFormat: true };
        } else {
            let currentLineIndex = -1;
            
            for (let i = 0; i < transcript.length; i++) {
                const line = transcript[i];
                const lineStartTime = line.startTime !== undefined 
                    ? line.startTime 
                    : (line.timestamp ? timestampToSeconds(line.timestamp) : 0);
                
                const nextLineStartTime = i < transcript.length - 1 
                    ? (transcript[i + 1].startTime !== undefined 
                        ? transcript[i + 1].startTime 
                        : (transcript[i + 1].timestamp ? timestampToSeconds(transcript[i + 1].timestamp) : Infinity))
                    : Infinity;
                
                if (currentTime >= lineStartTime && currentTime < nextLineStartTime) {
                    currentLineIndex = i;
                    break;
                }
            }
            
            if (currentLineIndex === -1 && transcript.length > 0) {
                const lastLine = transcript[transcript.length - 1];
                const lastLineStartTime = lastLine.startTime !== undefined 
                    ? lastLine.startTime 
                    : timestampToSeconds(lastLine.timestamp);
                if (currentTime >= lastLineStartTime) {
                    currentLineIndex = transcript.length - 1;
                }
            }
            
            const linesToShow = [];
            
            if (currentLineIndex >= 0) {
                if (currentLineIndex > 0) {
                    linesToShow.push({ 
                        index: currentLineIndex - 1, 
                        type: 'prev',
                        line: transcript[currentLineIndex - 1]
                    });
                }
                linesToShow.push({ 
                    index: currentLineIndex, 
                    type: 'current',
                    line: transcript[currentLineIndex]
                });
                if (currentLineIndex < transcript.length - 1) {
                    linesToShow.push({ 
                        index: currentLineIndex + 1, 
                        type: 'next',
                        line: transcript[currentLineIndex + 1]
                    });
                }
            } else if (transcript.length > 0) {
                linesToShow.push({ 
                    index: 0, 
                    type: 'next',
                    line: transcript[0]
                });
            }
            
            return { linesToShow, currentIndex: currentLineIndex, isNewFormat: false };
        }
    }, [transcriptData, currentTime, getTranscriptWordStart]);

    // Check visibility of active word
    const checkActiveWordVisibility = useCallback(() => {
        if (!showTranscript || transcriptWords.currentIndex < 0) return;
        
        const id = transcriptWords.isNewFormat
            ? `transcript-word-${transcriptWords.currentIndex}`
            : `transcript-line-${transcriptWords.currentIndex}`;

        const element = document.getElementById(id);
        const container = document.getElementById('global-transcript-container');

        if (element && container) {
            const containerRect = container.getBoundingClientRect();
            const elementRect = element.getBoundingClientRect();

            const isVisible = (
                elementRect.top >= containerRect.top - 20 &&
                elementRect.bottom <= containerRect.bottom + 20
            );

            setActiveWordInView(prev => (prev === isVisible ? prev : isVisible));
        }
    }, [showTranscript, transcriptWords.currentIndex, transcriptWords.isNewFormat]);

    // Update visibility and auto-scroll
    useEffect(() => {
        checkActiveWordVisibility();
        const livePlaying = getLiveIsPlaying();
        
        if (showTranscript && isAutoScrollEnabled && transcriptWords.currentIndex >= 0) {
            const scrollTargetId = transcriptWords.isNewFormat
                ? `transcript-word-${transcriptWords.currentIndex}`
                : `transcript-line-${transcriptWords.currentIndex}`;
            const currentElement = document.getElementById(scrollTargetId);
            const container = document.getElementById('global-transcript-container');

            if (currentElement && container) {
                const containerRect = container.getBoundingClientRect();
                const elementRect = currentElement.getBoundingClientRect();
                const scrollTop = container.scrollTop;
                const elementOffset = elementRect.top - containerRect.top + scrollTop;
                const centerOffset = (containerRect.height / 2) - (elementRect.height / 2);
                const targetTop = elementOffset - centerOffset;

                // Avoid constant micro-scroll corrections that cause shaking.
                // Reduced threshold (10px) to ensure it moves more predictably with the audio.
                if (Math.abs(container.scrollTop - targetTop) > 10) {
                    container.scrollTo({
                        top: targetTop,
                        behavior: livePlaying ? 'auto' : 'smooth'
                    });
                }
            }
        }
    }, [transcriptWords.currentIndex, showTranscript, checkActiveWordVisibility, isAutoScrollEnabled, transcriptWords.isNewFormat]);

    const handleUserScrollInteraction = () => {
        if (isAutoScrollEnabled) {
            setIsAutoScrollEnabled(false);
        }
    };

    const closeAudioPlayer = (event) => {
        if (closeInProgressRef.current) return;
        closeInProgressRef.current = true;

        if (event) {
            event.preventDefault();
            event.stopPropagation();
        }

        justClosedUntilRef.current = Date.now() + 900;

        console.log('[GlobalAudioPlayer] Close button clicked');
        
        // Mark as inactive IMMEDIATELY to block any async retries/playbacks
        isPlayerActiveRef.current = false;
        isPlayerClosedByMemberRef.current = true;
        
        // Cancel any pending restore attempts
        if (restoreTimeoutRef.current) {
            clearTimeout(restoreTimeoutRef.current);
            restoreTimeoutRef.current = null;
        }
        
        // Cancel any pending retry attempts
        if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
        }

        if (setupAudioTimeoutRef.current) {
            clearTimeout(setupAudioTimeoutRef.current);
            setupAudioTimeoutRef.current = null;
        }

        if (directLoadTimeoutRef.current) {
            clearTimeout(directLoadTimeoutRef.current);
            directLoadTimeoutRef.current = null;
        }

        if (fallbackPlayTimeoutRef.current) {
            clearTimeout(fallbackPlayTimeoutRef.current);
            fallbackPlayTimeoutRef.current = null;
        }
        
        isRestoringRef.current = false;
        
        // Stop and clear audio
        if (audioRef.current) {
            // Nullify all handlers to prevent race conditions
            audioRef.current.oncanplay = null;
            audioRef.current.oncanplaythrough = null;
            audioRef.current.onloadedmetadata = null;
            audioRef.current.onerror = null;
            
            audioRef.current.pause();
            audioRef.current.src = ''; // Clear source to stop buffering/loading
            audioRef.current.load(); // Force reset
        }
        
        // Clear all state
        setAudioPlayerVisible(false);
        setIsPlaying(false);
        setCurrentPodcast(null);
        setCurrentTime(0);
        setDisplayTimeSec(0);
        lastDisplaySecondRef.current = -1;
        setCorrectedThumbnailUrl(null);
        
        // Remove from sessionStorage and cookie with a flag to prevent restoration in this session
        sessionStorage.removeItem(STORAGE_KEY);
        removeCrossSubdomainState(STORAGE_KEY);
        sessionStorage.setItem(STORAGE_KEY + '_closed', 'true');
        setCrossSubdomainState(STORAGE_KEY + '_closed', 'true');

        if (navigator.vibrate) navigator.vibrate(200);

        console.log('[GlobalAudioPlayer] Audio player closed and state cleared');

        setTimeout(() => {
            closeInProgressRef.current = false;
        }, 200);
    };

    // Global Alt key longpress handler
    useEffect(() => {
        const altKeyDownRefGlobal = { current: false };
        let altPressTimerGlobal = null;

        const handleKeyDown = (event) => {
            if (event.key === 'Alt') {
                event.preventDefault(); // Prevent browser menu focus
                if (!altKeyDownRefGlobal.current) {
                    altKeyDownRefGlobal.current = true;
                    if (altPressTimerGlobal) clearTimeout(altPressTimerGlobal);
                    altPressTimerGlobal = setTimeout(() => {
                        document.body.classList.add('show-shortcuts');
                    }, 1000);
                }
            }
        };

        const handleKeyUp = (event) => {
            if (event.key === 'Alt') {
                event.preventDefault();
                altKeyDownRefGlobal.current = false;
                if (altPressTimerGlobal) {
                    clearTimeout(altPressTimerGlobal);
                    altPressTimerGlobal = null;
                }
                document.body.classList.remove('show-shortcuts');
            }
        };

        const handleBlur = () => {
            altKeyDownRefGlobal.current = false;
            if (altPressTimerGlobal) {
                clearTimeout(altPressTimerGlobal);
                altPressTimerGlobal = null;
            }
            document.body.classList.remove('show-shortcuts');
        };

        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        window.addEventListener('blur', handleBlur);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
            window.removeEventListener('blur', handleBlur);
            if (altPressTimerGlobal) {
                clearTimeout(altPressTimerGlobal);
            }
            document.body.classList.remove('show-shortcuts');
        };
    }, []);

    // Keyboard shortcuts handler when player is visible
    useEffect(() => {
        if (!audioPlayerVisible || !currentPodcast) {
            return;
        }

        const handleKeyDown = (event) => {
            const key = event.key;

            if (key === 'Alt') {
                return;
            }

            // Check if user is typing in an input, textarea, or contenteditable
            const activeElement = document.activeElement;
            if (activeElement) {
                const tagName = activeElement.tagName.toLowerCase();
                const isEditable = tagName === 'input' || 
                                   tagName === 'textarea' || 
                                   activeElement.getAttribute('contenteditable') === 'true';
                if (isEditable) {
                    return; // Ignore shortcuts when typing in inputs/textareas
                }
            }

            if (key === ' ') {
                // Space bar to toggle play/pause
                event.preventDefault(); // Prevent page scrolling
                void togglePlayPause();
            } else if (key === 'Escape') {
                // Escape key to close the player
                event.preventDefault();
                closeAudioPlayer(event);
            } else if (key === 'm' || key === 'M') {
                // 'm' key to toggle mute
                event.preventDefault();
                handleVolumeChange(volume > 0 ? 0 : (lastNonZeroVolumeRef.current || 1));
            } else if (key === 't' || key === 'T') {
                // 't' key to toggle transcript
                event.preventDefault();
                const next = !showTranscript;
                setShowTranscript(next);
                if (next && !transcriptData && !transcriptLoading && currentPodcast) {
                    void fetchTranscript(currentPodcast.slug);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        audioPlayerVisible,
        currentPodcast,
        showTranscript,
        transcriptData,
        transcriptLoading,
        volume,
        togglePlayPause,
        handleVolumeChange,
        closeAudioPlayer,
        fetchTranscript
    ]);

    // Always render the audio element (even when hidden) so it's available when needed
    const audioElement = (
        <audio
            ref={audioRef}
            onTimeUpdate={handleTimeUpdate}
            preload="metadata"
            hidden
        />
    );

    // Only show player UI if visible and has podcast
    if (!audioPlayerVisible || !currentPodcast) {
        return audioElement;
    }

    const effectiveIsPlaying = getLiveIsPlaying() || isPlaying;

    return (
        <div className="audio-player fixed bottom-0 left-0 right-0 z-[2900] border-t border-slate-200 bg-white/95 backdrop-blur-md">
            {audioElement}
            <div className='player-thumbnail'>
                <img 
                    src={correctedThumbnailUrl || (currentPodcast && currentPodcast.thumbnail) || ''} 
                    alt={(currentPodcast && currentPodcast.title) || ''}
                />
            </div>

            <div className="audio-details-with-player">
                <div className="title-with-closeBnt">
                    <h3 className="pod-title">
                        {(() => {
                            if (!currentPodcast) return '';
                            // Try to extract episode number - more flexible regex
                            // Matches: /Podcast-12/, /episode-12/, /Episode+12/, /Podcast+%2312/
                            const audioMatch = currentPodcast.audioFile ? currentPodcast.audioFile.match(/(?:Podcast|Episode)(?:[-+._\s]|%20|%23)+(\d+)/i) : null;
                            const episodeNumber = audioMatch ? audioMatch[1] : null;
                            
                            const titleContent = episodeNumber 
                                ? (
                                    <>
                                        <span className="episode-prefix">Show #{episodeNumber}</span>
                                        <span className="title-separator">-</span>
                                        <span className="episode-title">{currentPodcast.title}</span>
                                    </>
                                )
                                : currentPodcast.title;

                            if (!currentPodcast.slug) return titleContent;
                            return (
                                <button
                                    type="button"
                                    className="pod-title-link"
                                    onClick={() => navigate(`/podcast/${currentPodcast.slug}`)}
                                    title="Open episode page"
                                >
                                    {titleContent}
                                </button>
                            );
                        })()}
                    </h3>
                    
                    <button className={`transcript-toggle-btn relative ${showTranscript ? 'active' : ''}`} onClick={() => {
                        const next = !showTranscript;
                        setShowTranscript(next);
                        if (next && !transcriptData && !transcriptLoading && currentPodcast) {
                            fetchTranscript(currentPodcast.slug);
                        }
                    }}>
                        <i className="fi fi-sr-message-quote transcript-icon"></i>
                        <span className="transcript-text">Transcript</span>
                        <span className="shortcut-badge">T</span>
                    </button>

                    <button
                        type="button"
                        className="close-button relative"
                        onPointerDown={(e) => {
                            closeAudioPlayer(e);
                            e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                closeAudioPlayer(e);
                            }
                        }}
                        onClick={closeAudioPlayer}
                        aria-label="Close audio player"
                    >
                        <i id="close_btn" className="fi fi-rr-cross-small"></i>
                        <span className="shortcut-badge">Esc</span>
                    </button>
                </div>

                {/* Transcript Feature starts */}
                {currentPodcast && showTranscript && (
                <div className={`transcript-preview`}>
                    {/* Transcript Header for Global Player */}
                     <div className="transcript-header">
                        <span className="transcript-header-text">
                            {currentChapter 
                                ? `Chapter ${currentChapter.index}: ${currentChapter.title}` 
                                : 'Follow the conversation'
                            }
                        </span>
                        {!activeWordInView && (
                            <button 
                                className="back-to-transcript-btn"
                                onClick={() => {
                                    if (transcriptWords.currentIndex >= 0) {
                                        const element = document.getElementById(
                                            transcriptWords.isNewFormat
                                                ? `transcript-word-${transcriptWords.currentIndex}`
                                                : `transcript-line-${transcriptWords.currentIndex}`
                                        );
                                        const container = document.getElementById('global-transcript-container');
                                        
                                        if (element && container) {
                                            const containerRect = container.getBoundingClientRect();
                                            const elementRect = element.getBoundingClientRect();
                                            const scrollTop = container.scrollTop;
                                            const elementOffset = elementRect.top - containerRect.top + scrollTop;
                                            const centerOffset = (containerRect.height / 2) - (elementRect.height / 2);
                                            
                                            container.scrollTo({
                                                top: elementOffset - centerOffset,
                                                behavior: 'smooth'
                                            });
                                            setActiveWordInView(true);
                                            setIsAutoScrollEnabled(true); // Re-enable sticky scroll
                                        }
                                    }
                                }}
                            >
                                Live Transcript
                            </button>
                        )}
                    </div>
                    <div
                        className="transcript-scroll-area"
                        id="global-transcript-container"
                        onScroll={checkActiveWordVisibility}
                        onWheel={handleUserScrollInteraction}
                        onTouchMove={handleUserScrollInteraction}
                    >
                        {transcriptLoading ? (
                            <p className="transcript-line" style={{ fontStyle: 'italic', color: '#666' }}>
                                Loading transcript...
                            </p>
                        ) : !transcriptData ? (
                            <p className="transcript-line" style={{ fontStyle: 'italic', color: '#666' }}>
                                No transcript available for this podcast.
                            </p>
                        ) : (
                            <TranscriptContent 
                                words={transcriptWords.words}
                                currentIndex={transcriptWords.currentIndex}
                                liveWindowStart={transcriptWords.liveWindowStart}
                                liveWindowEnd={transcriptWords.liveWindowEnd}
                                isNewFormat={transcriptWords.isNewFormat}
                                linesToShow={transcriptWords.linesToShow}
                            />
                        )}
                    </div>
                </div>
                )}
                {/* Transcript Feature ends */}

                <div className="audio-controls">
                    <div className="audio-controls-left">
                        {/* New Tape Progress Bar */}
                        <div 
                            className={`tape-container ${isDragging ? 'dragging' : ''}`}
                            ref={tapeContainerRef}
                            onMouseDown={handleMouseDown}
                            onTouchStart={handleTouchStart}
                        >
                            <div className="tape-center-line">
                                <span className="tape-playhead-timestamp">
                                    {formatTime(isDragging ? dragTime : displayTimeSec)}
                                </span>
                            </div>
                            
                            <TapeScroller 
                                audioRef={audioRef}
                                isPlaying={isPlaying && !isDragging}
                                isDragging={isDragging}
                                duration={duration}
                                tapeMarkers={tapeMarkers}
                                currentTimeVal={isDragging ? dragTime : currentTime}
                            />
                        </div>
                        
                        {/* Audio Progress Stats */}
                        {duration > 0 && (
                            <div className="audio-progress-stats">
                                <span className="stats-text">
                                    Played: {Math.floor((displayTimeSec / duration) * 100)}%
                                </span>
                                <span className="stats-text">
                                    {formatTime(Math.max(0, duration - displayTimeSec))} left
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="audio-controls-right-wrapper">
                        <div className="audio-top-controls">
                            {/* Volume Control */}
                            <div className="volume-control-container">
                                <button 
                                    type="button"
                                    className="volume-toggle-btn relative"
                                    onClick={() => handleVolumeChange(volume > 0 ? 0 : 1)}
                                    title={volume > 0 ? "Mute" : "Unmute"}
                                >
                                    <i className={`fi ${volume > 0 ? "fi-ss-volume-down" : "fi-ss-volume-mute"} volume-icon`}></i>
                                    <span className="shortcut-badge">M</span>
                                </button>
                            </div>

                            <div className="speed-control-container" ref={speedControlRef}>
                                <button
                                    type="button"
                                    className="speed-control-btn"
                                    onClick={() => setShowSpeedPopup(v => !v)}
                                    title="Playback speed"
                                >
                                    {playbackRate}x
                                </button>
                                {showSpeedPopup && (
                                    <div className="speed-control-popup" role="menu" aria-label="Playback speed options">
                                        <div className="speed-slider-container">
                                            <button 
                                                type="button" 
                                                className="speed-adjust-btn"
                                                onClick={() => {
                                                    const next = Math.max(0.5, Math.round((playbackRate - 0.1) * 10) / 10);
                                                    setPlaybackRate(next);
                                                    if (audioRef.current) audioRef.current.playbackRate = next;
                                                }}
                                                aria-label="Decrease speed"
                                            >
                                                <i className="fi fi-ss-minus"></i>
                                            </button>
                                            <span className="speed-value-display">{playbackRate.toFixed(1)}x</span>
                                            <button 
                                                type="button" 
                                                className="speed-adjust-btn"
                                                onClick={() => {
                                                    const next = Math.min(2.0, Math.round((playbackRate + 0.1) * 10) / 10);
                                                    setPlaybackRate(next);
                                                    if (audioRef.current) audioRef.current.playbackRate = next;
                                                }}
                                                aria-label="Increase speed"
                                            >
                                                <i className="fi fi-ss-plus"></i>
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="audio-controls-right">
                             {/* Skip Backward 60s (Replacing Prev Episode) */}
                            <button className="skip-buttons skip-backward-60" onClick={handleSkipBackward60}>
                                <i className="fi fi-sr-rewind"></i>
                                <p className="text">60</p>
                            </button>

                            <button className="skip-buttons skip-backward" onClick={backwardAudio}>
                                <i className="fi fi-sr-left"></i>
                                <p className="text">15</p>
                            </button>
                            <button className="audio-play-button relative" onClick={togglePlayPause}>
                                {isBuffering ? (
                                    <div className="audio-loading-waveform">
                                        <div className="audio-loading-bar"></div>
                                        <div className="audio-loading-bar"></div>
                                        <div className="audio-loading-bar"></div>
                                        <div className="audio-loading-bar"></div>
                                    </div>
                                ) : (
                                    <i className={effectiveIsPlaying ? "fi fi-sr-pause" : "fi fi-sr-play"} aria-hidden="true"></i>
                                )}
                                <span className="shortcut-badge">Space</span>
                            </button>
                            <button className="skip-buttons skip-forward" onClick={forwardAudio}>
                                <i className="fi fi-sr-right"></i>
                                <p className="text">15</p>
                            </button>

                            {/* Skip Forward 60s (Replacing Next Episode) */}
                            <button className="skip-buttons skip-forward-60" onClick={handleSkipForward60}>
                                <i className="fi fi-sr-rewind rotate-180"></i>
                                <p className="text">60</p>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// Helper function to trigger podcast play from anywhere
export const playPodcastGlobal = (podcast, startTime = 0) => {
    console.log('[playPodcastGlobal] Triggering global playback for:', {
        id: podcast ? (podcast._id || podcast.slug) : 'null',
        title: podcast ? podcast.title : 'null',
        startTime: startTime
    });
    
    if (!podcast) {
        console.error('[playPodcastGlobal] ERROR: Podcast is null or undefined');
        return;
    }

    if (!podcast.audioFile) {
        console.error('[playPodcastGlobal] ERROR: Podcast audioFile is missing');
        return;
    }

    let resolvedStartTime = startTime;
    if (!resolvedStartTime) {
        const savedProgress = getPodcastProgress(podcast);
        if (savedProgress && savedProgress.started && !savedProgress.completed && savedProgress.currentTime > 1) {
            resolvedStartTime = savedProgress.currentTime;
        }
    }

    // Attach start time to podcast object for event detail
    const podcastWithStart = { ...podcast, startTime: resolvedStartTime };
    
    console.log('[playPodcastGlobal] Dispatching playPodcast event');
    const event = new CustomEvent('playPodcast', { detail: podcastWithStart });
    window.dispatchEvent(event);
    console.log('[playPodcastGlobal] Event dispatched successfully');
};

